import { Box, styled } from "@mui/material";
import { useEffect, useRef } from "react";

import { degToRad } from "utils/math";

import {
    ahCcw,
    ahCw,
    hintIn,
    hintOut,
    INDICATOR_CIRCLE_RADIUS,
    orbitDirSwitch,
    orbitOpacity,
    PULSE_DURATION,
    TOOLTIP_FADE,
} from "./navigationAnimations";

// ─── Tooltip ───────────────────────────────────────────────

const TooltipBox = styled(Box, { shouldForwardProp: (prop) => prop !== "visible" })<{ visible: boolean }>(
    ({ visible }) => ({
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 10,
        opacity: visible ? 1 : 0,
        transition: `opacity ${TOOLTIP_FADE}ms ease`,
        background: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(4px)",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        color: "#fff",
        padding: "6px 10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        whiteSpace: "nowrap",
    }),
);

interface NavigationTooltipProps {
    visible: boolean;
    text: string;
    mouseX: number;
    mouseY: number;
}

export function NavigationTooltip({ visible, text, mouseX, mouseY }: NavigationTooltipProps) {
    return (
        <TooltipBox visible={visible} style={{ transform: `translate(${mouseX + 18}px, ${mouseY + 4}px)` }}>
            {text}
        </TooltipBox>
    );
}

// ─── Pulse Ring ────────────────────────────────────────────

const PULSE_SVG_SIZE = INDICATOR_CIRCLE_RADIUS * 2;
const PULSE_SVG_HALF = INDICATOR_CIRCLE_RADIUS;

interface PulseRingProps {
    x: number;
    y: number;
    onComplete: () => void;
}

export function PulseRing({ x, y, onComplete }: PulseRingProps) {
    const svgRef = useRef<SVGSVGElement>(null);
    const circleRef = useRef<SVGCircleElement>(null);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    useEffect(() => {
        const svg = svgRef.current;
        const circle = circleRef.current;
        if (!svg || !circle) {
            return;
        }

        const svgAnim = svg.animate(
            [
                { transform: "scale(0.15)", opacity: 0.3 },
                { transform: "scale(5)", opacity: 0 },
            ],
            { duration: PULSE_DURATION, easing: "cubic-bezier(0.2, 0.8, 0.4, 1)", fill: "forwards" },
        );

        circle.animate([{ strokeWidth: "2" }, { strokeWidth: "0.2" }], {
            duration: PULSE_DURATION,
            easing: "cubic-bezier(0.2, 0.8, 0.4, 1)",
            fill: "forwards",
        });

        svgAnim.onfinish = () => onCompleteRef.current();
        return () => svgAnim.cancel();
    }, []);

    return (
        <svg
            ref={svgRef}
            width={PULSE_SVG_SIZE}
            height={PULSE_SVG_SIZE}
            viewBox={`${-PULSE_SVG_HALF} ${-PULSE_SVG_HALF} ${PULSE_SVG_SIZE} ${PULSE_SVG_SIZE}`}
            style={{
                position: "absolute",
                left: x - PULSE_SVG_HALF,
                top: y - PULSE_SVG_HALF,
                overflow: "visible",
                pointerEvents: "none",
                zIndex: 5,
                transformOrigin: "center",
                filter: "drop-shadow(0 0 4px rgba(147,210,255,0.45))",
                opacity: 0.3,
                transform: "scale(0.15)",
            }}
        >
            <circle
                ref={circleRef}
                cx={0}
                cy={0}
                r={INDICATOR_CIRCLE_RADIUS}
                fill="none"
                stroke="rgba(147,210,255,0.9)"
                vectorEffect="non-scaling-stroke"
                strokeWidth={2}
            />
        </svg>
    );
}

// ─── Rotation Hints ────────────────────────────────────────

const ORBIT_RADIUS = (INDICATOR_CIRCLE_RADIUS + 14) * 1.775;
const ARC_SPAN_DEG = 60;
const ARROW_ARM = 6;
const ARROW_HALF_W = 5.5;

function arcPath(startDeg: number, endDeg: number, r: number): string {
    const x1 = r * Math.cos(degToRad(startDeg));
    const y1 = r * Math.sin(degToRad(startDeg));
    const x2 = r * Math.cos(degToRad(endDeg));
    const y2 = r * Math.sin(degToRad(endDeg));
    const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    const sweep = endDeg > startDeg ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} ${sweep} ${x2} ${y2}`;
}

function chevronPoints(cx: number, cy: number, angleDeg: number, direction: "cw" | "ccw"): string {
    const tangent = degToRad(angleDeg + (direction === "cw" ? 90 : -90));
    const tipX = cx + ARROW_ARM * Math.cos(tangent);
    const tipY = cy + ARROW_ARM * Math.sin(tangent);
    const perpAngle = tangent + Math.PI / 2;
    const x1 = cx + ARROW_HALF_W * Math.cos(perpAngle);
    const y1 = cy + ARROW_HALF_W * Math.sin(perpAngle);
    const x2 = cx - ARROW_HALF_W * Math.cos(perpAngle);
    const y2 = cy - ARROW_HALF_W * Math.sin(perpAngle);
    return `M ${x1} ${y1} L ${tipX} ${tipY} L ${x2} ${y2}`;
}

const HintsSvg = styled("svg", { shouldForwardProp: (prop) => prop !== "exiting" })<{ exiting: boolean }>(
    ({ exiting }) => ({
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 5,
        overflow: "visible",
        animation: exiting ? `${hintOut} 350ms ease-out forwards` : `${hintIn} 450ms ease-out both`,
    }),
);

const OrbitGroup = styled("g")({
    animation: `${orbitDirSwitch} 4s ease-in-out infinite, ${orbitOpacity} 4s ease-in-out infinite`,
});

const CwArrowhead = styled("path")({
    animation: `${ahCw} 4s linear infinite`,
});

const CcwArrowhead = styled("path")({
    animation: `${ahCcw} 4s linear infinite`,
});

interface RotationHintsProps {
    x: number;
    y: number;
    exiting: boolean;
    onExitComplete: () => void;
}

export function RotationHints({ x, y, exiting, onExitComplete }: RotationHintsProps) {
    const r = ORBIT_RADIUS;
    const arc1Start = -ARC_SPAN_DEG;
    const arc1End = ARC_SPAN_DEG;
    const arc2Start = 180 - ARC_SPAN_DEG;
    const arc2End = 180 + ARC_SPAN_DEG;

    const endPt1X = r * Math.cos(degToRad(arc1End));
    const endPt1Y = r * Math.sin(degToRad(arc1End));
    const startPt1X = r * Math.cos(degToRad(arc1Start));
    const startPt1Y = r * Math.sin(degToRad(arc1Start));

    const endPt2X = r * Math.cos(degToRad(arc2End));
    const endPt2Y = r * Math.sin(degToRad(arc2End));
    const startPt2X = r * Math.cos(degToRad(arc2Start));
    const startPt2Y = r * Math.sin(degToRad(arc2Start));

    return (
        <HintsSvg
            exiting={exiting}
            onAnimationEnd={(e) => {
                if (exiting && e.animationName.includes("hintOut")) {
                    onExitComplete();
                }
            }}
            style={{ transform: `translate(${x}px, ${y}px)` }}
            width="1"
            height="1"
        >
            <g filter="url(#hint-shadow)">
                <OrbitGroup>
                    <path
                        d={arcPath(arc1Start, arc1End, r)}
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3.6"
                        strokeLinecap="round"
                    />
                    <CwArrowhead
                        d={chevronPoints(endPt1X, endPt1Y, arc1End, "cw")}
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <CcwArrowhead
                        d={chevronPoints(startPt1X, startPt1Y, arc1Start, "ccw")}
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </OrbitGroup>

                <OrbitGroup>
                    <path
                        d={arcPath(arc2Start, arc2End, r)}
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3.6"
                        strokeLinecap="round"
                    />
                    <CwArrowhead
                        d={chevronPoints(endPt2X, endPt2Y, arc2End, "cw")}
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <CcwArrowhead
                        d={chevronPoints(startPt2X, startPt2Y, arc2Start, "ccw")}
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </OrbitGroup>
            </g>

            <defs>
                <filter id="hint-shadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="rgba(0,0,0,0.55)" />
                </filter>
            </defs>
        </HintsSvg>
    );
}
